import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';

const MESSAGES = [
  'Loading...',
  'Getting things ready...',
  'Almost there...',
];

/**
 * Premium, on-brand loading screen. Use `fullScreen` (default) for initial
 * app boot / full-page navigation; pass `fullScreen={false}` to fill a
 * section (e.g. the Reader viewport) instead of the whole page.
 *
 * Wrap the conditional render in <AnimatePresence> at the call site to get
 * the fade-out on unmount — this component only handles fade-in on mount.
 */
export default function LoadingScreen({ message, fullScreen = true, className = '' }) {
  const text = message || MESSAGES[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className={[
        fullScreen ? 'fixed inset-0 z-[999]' : 'absolute inset-0',
        'flex flex-col items-center justify-center gap-6',
        'bg-white/70 dark:bg-surface-dark/80 backdrop-blur-xl',
        className,
      ].join(' ')}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* soft gradient wash behind everything */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-mesh opacity-40 dark:opacity-[0.08]" />

      <div className="relative flex items-center justify-center">
        {/* breathing glow */}
        <motion.div
          className="absolute h-24 w-24 rounded-full bg-gradient-primary blur-2xl"
          animate={{ opacity: [0.35, 0.6, 0.35], scale: [0.9, 1.05, 0.9] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* rotating ring */}
        <motion.div
          className="absolute h-16 w-16 rounded-full border-2 border-transparent"
          style={{
            borderTopColor: 'rgba(139, 92, 246, 0.85)',
            borderRightColor: 'rgba(236, 72, 153, 0.35)',
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
        />

        {/* brand mark */}
        <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-primary text-white shadow-glow">
          <BookOpen size={20} strokeWidth={2.5} />
        </span>
      </div>

      <motion.p
        key={text}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400"
      >
        {text}
      </motion.p>
    </motion.div>
  );
}
